import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { toastSuccess, toastError } from '../utils/toasts';
import Input from './atoms/Input';
import Button from './atoms/Button';
import {
  RootState,
  useCreateBusinessMutation,
  useGetBusinessByIdQuery,
  useUpdateBusinessMutation,
  setSelectedBusiness,
} from '../store';
import CustomModal from './atoms/CustomModal';
import { BusinessDTO } from '../models/business';
import LandingEventCard from './LandingEventCard';
import Checkbox from './atoms/Checkbox';
import Switch from './atoms/Switch';
import TermsAndConditions from './TermsAndConditions';
import dummyEvent from '../utils/dummyEvent';
import TermsAndServicesType from '../models/terms';
import LoadingLottie from './atoms/LoadingLottie';
import AdminSectionTitle from './AdminSectionTitle';
import PhotoUpload from './PhotoUpload';
import useQuery from '../hooks/useQuery';
import OnBoardingCard from './OnBoardingCard';
import OnBoardingScreens from '../pages/OnBoardingScreens';
import { IconPlay2 } from './icons';

const getValidationSchema = (userRole: string) => {
  const baseSchema = {
    name: yup.string().required('Debes ingresar un nombre'),
    legal_name: yup.string().required('Debes ingresar un nombre y apellido o razón social'),
    active: yup.boolean().required(),
    email: yup.string().email('Debes ingresar un correo electrónico válido').required('Debes ingresar un correo electrónico'),
    phone: yup.string()
      .required('Debes ingresar un número de teléfono')
      .test('is-valid-phone', 'Ingresá un celular válido', (value) => {
        if (!value) {
          return false;
        }
        const pn = parsePhoneNumberFromString(value);
        return !!pn && pn.isValid();
      }),
    fiscal_id: yup.string().test('is-number', 'Debes ingresar un DNI o CUIT válido', (value) => {
      if (!value) {
        return true;
      }
      return /^\d+$/.test(value.toString());
    }),
    accepted_terms: yup.boolean().oneOf([true], 'Debes aceptar los términos y condiciones').required('Debes aceptar los términos y condiciones'),
    description: yup.string().optional(),
  };

  if (userRole === 'ROOT') {
    return yup.object().shape({
      ...baseSchema,
      has_mp_split: yup.boolean().required(),
      online_service_charge: yup.number().required('Debes ingresar una comisión').typeError('Debes ingresar una comisión'),
      manual_service_charge: yup.number().required('Debes ingresar una comisión').typeError('Debes ingresar una comisión'),
      is_reliable: yup.boolean().required(),
      nickname: yup.string().nullable(),
      has_cash_registers: yup.boolean().required(),
      has_members: yup.boolean().required(),
      has_subscription: yup.boolean().required(),
      is_test: yup.boolean().required(),
      is_white_label: yup.boolean().required(),
    });
  }

  return yup.object().shape(baseSchema);
};

interface FormData {
  name: string;
  legal_name: string;
  email: string;
  phone: string;
  fiscal_id: string | undefined;
  accepted_terms: boolean;
  active: boolean;
  has_mp_split?: boolean;
  is_reliable?: boolean;
  online_service_charge?: number;
  manual_service_charge?: number;
  nickname?: string | null;
  description: string | undefined;
  can_request_advances?: boolean;
  has_cash_registers?: boolean;
  has_members?: boolean;
  logo?: string;
  has_subscription?: boolean;
  is_test?: boolean;
  is_white_label?: boolean;
}

interface BusinessEditorProps {
  create?: boolean;
  onBoarding?: boolean;
}

const BusinessEditor = ({ create = false, onBoarding = false }: BusinessEditorProps) => {
  const userData = useSelector((state: RootState) => state.auth.user);
  const userRole = userData?.type === 'ROOT' ? 'ROOT' : 'USER';
  const {
    handleSubmit, reset, formState: { errors }, watch, setValue, control,
  } = useForm<FormData>({
    resolver: yupResolver(getValidationSchema(userRole)),
    mode: 'onChange',
    defaultValues: {
      name: '',
      legal_name: '',
      email: '',
      phone: '',
      fiscal_id: '',
      accepted_terms: false,
      active: true,
      has_mp_split: false,
      is_reliable: false,
      nickname: null,
      description: '',
      has_cash_registers: false,
      has_members: false,
      logo: '',
      has_subscription: false,
      is_test: false,
      is_white_label: false,
    },
  });
  const query = useQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const onboardingProducer = query.get('onboarding-producer') === 'true' || false;
  const [createBusiness, createBusinessRes] = useCreateBusinessMutation();
  const [updateBusiness, updateBusinessRes] = useUpdateBusinessMutation();
  const [isOnBoardingOpen, setIsOnBoardingOpen] = useState(onboardingProducer);
  const [isOpen, setIsOpen] = useState(false);
  const [businessData, setBusinessData] = useState<BusinessDTO>();
  const { business_id: businessId } = useParams();
  const { data: retrievedBusiness, isLoading } = useGetBusinessByIdQuery(businessId ?? '', { skip: !businessId || (create && !onBoarding) });
  const [hasDomainSetup, setHasDomainSetup] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [hasUnsavedPhoto, setHasUnsavedPhoto] = useState(false);

  useEffect(() => {
    if (onBoarding) {
      if (userData) {
        reset({
          legal_name: userData.username,
          email: userData.email,
          phone: userData.phone_number,
          active: true,
          accepted_terms: false,
          name: '',
          fiscal_id: '',
          description: '',
          ...(userData?.type === 'ROOT' && {
            has_mp_split: false,
            is_reliable: false,
            online_service_charge: 0.15,
            manual_service_charge: 0.05,
            has_cash_registers: false,
            has_members: false,
            has_subscription: false,
            is_test: false,
            is_white_label: false,
          }),
        });
      }
    } else if (!create && retrievedBusiness) {
      setBusinessData(retrievedBusiness);
      reset({
        name: retrievedBusiness.name,
        legal_name: retrievedBusiness.legal_name || '',
        email: retrievedBusiness.email || '',
        phone: retrievedBusiness.phone_number || '',
        fiscal_id: retrievedBusiness.fiscal_id || '',
        active: retrievedBusiness.active,
        accepted_terms: true,
        description: retrievedBusiness.description || '',
        ...(userData?.type === 'ROOT' && {
          has_mp_split: retrievedBusiness.has_mp_split || false,
          is_reliable: retrievedBusiness.is_reliable || false,
          online_service_charge: retrievedBusiness.online_service_charge,
          manual_service_charge: retrievedBusiness.manual_service_charge,
          nickname: retrievedBusiness.nickname,
          can_request_advances: retrievedBusiness.can_request_advances || false,
          has_cash_registers: retrievedBusiness.has_cash_registers || false,
          has_members: retrievedBusiness.has_members || false,
          logo: retrievedBusiness.logo,
          has_subscription: retrievedBusiness.has_subscription || false,
          is_test: retrievedBusiness.is_test || false,
          is_white_label: retrievedBusiness.is_white_label || false,
        }),
      });
      setHasDomainSetup(!!retrievedBusiness.nickname);
    } else {
      reset({
        legal_name: userData?.username || '',
        email: userData?.email || '',
        phone: userData?.phone_number || '',
        fiscal_id: '',
        active: true,
        accepted_terms: true,
        ...(userData?.type === 'ROOT' && {
          has_mp_split: false,
          is_reliable: false,
          online_service_charge: 0.15,
          manual_service_charge: 0.05,
          can_request_advances: false,
          has_cash_registers: false,
          has_members: false,
          has_subscription: false,
          is_test: false,
          is_white_label: false,
        }),
      });
    }
  }, [userData, retrievedBusiness, create, onBoarding, reset]);

  useEffect(() => {
    if (updateBusinessRes.isSuccess) {
      toastSuccess('Productora actualizada correctamente');
      if (updateBusinessRes.data?.data) {
        dispatch(setSelectedBusiness(updateBusinessRes.data.data));
      }
      navigate(`/admin/${businessId}/events`);
    }
  }, [updateBusinessRes.isSuccess]);

  const formDataToDto = (data: FormData) => ({
    name: data.name,
    description: data.description || '',
    active: data.active,
    legal_name: data.legal_name,
    fiscal_id: data.fiscal_id,
    phone_number: data.phone,
    accepted_terms: data.accepted_terms,
    email: data.email,
    ...(userData?.type === 'ROOT' && {
      has_mp_split: data.has_mp_split || false,
      is_reliable: data.is_reliable || false,
      online_service_charge: data.online_service_charge,
      manual_service_charge: data.manual_service_charge,
      nickname: data.nickname || null,
      can_request_advances: data.can_request_advances || false,
      has_cash_registers: data.has_cash_registers || false,
      has_members: data.has_members || false,
      logo: data.logo || '',
      has_subscription: data.has_subscription || false,
      is_test: data.is_test || false,
      is_white_label: data.is_white_label || false,
    }),
  });

  const onSubmit = async (data: FormData) => {
    if (hasUnsavedPhoto) {
      toastError('Por favor, sube la foto antes de guardar los cambios');
      return;
    }
    try {
      if (create || onBoarding) {
        const businessDto = formDataToDto(data);
        const res = await createBusiness({
          business: businessDto,
        });
        console.log('Create business response:', res);
        if ('data' in res && res.data.data) {
          toastSuccess('Productora creada correctamente');
          setBusinessData(res.data.data);
          if (onBoarding) {
            setIsOpen(true);
          } else {
            navigate(`/admin/${res.data.data.id}/events`);
          }
        } else if ('error' in res) {
          toastError('Ocurrió un error al crear la productora');
        }
      } else {
        const res = await updateBusiness({
          business: { ...formDataToDto(data), id: businessId || '0' },
        });
        if ('error' in res) {
          console.error('Error updating business:', res.error);
          toastError('Ocurrió un error al actualizar la productora');
        }
      }
    } catch (e) {
      console.error(e);
      toastError('Ocurrió un error al guardar la productora');
    }
  };

  if (isLoading && !onBoarding) {
    return <LoadingLottie />;
  }

  const nameValue = watch('name') || '';

  return (
    <form className={`flex flex-col gap-8 ${!onBoarding ? 'my-6' : ''}`} onSubmit={handleSubmit(onSubmit)}>
      {onboardingProducer && <OnBoardingCard
        cards={OnBoardingScreens}
        mainButton={{ title: 'Comenzar', onClick: () => setIsOnBoardingOpen(false) }}
        secondaryButton={{ title: 'Omitir', onClick: () => setIsOnBoardingOpen(false) }}
        open={isOnBoardingOpen}
      />}
      {!onBoarding && (
        <AdminSectionTitle
          title={businessData?.name || 'Productoras'}
          subtitle={`${create ? 'Crear' : 'Editar'} productora`}
          icon={<IconPlay2 className='w-12 h-12 rotate-180' />}
        />
      )}
      <div className="flex flex-col md:flex-row gap-3 glass-surface">
        <div className='md:w-1/2'>
          <h2 className="text-xl font-medium text-text-primary">Datos de la productora</h2>
          <div className='mt-4'>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Nombre de la productora *"
                  id="name"
                  labelAbove
                  errMsg={errors.name?.message}
                />
              )}
            />
          </div>
          <p className='text-sm my-4'>Con este nombre se visualizará también como organizador en la vista del evento para los clientes</p>
          <p className='text-sm'>Aquí te dejamos un ejemplo de como se vería el nombre de tu productora en nuestra home de eventos</p>

          {!onBoarding && (
            <div className='mt-4'>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="description"
                    label="Descripción"
                    labelAbove={true}
                    errMsg={errors.description?.message}
                  />
                )}
              />
            </div>
          )}
        </div>
        <div className='md:w-1/2 max-w-2xl mx-auto'>
          <LandingEventCard event={dummyEvent(nameValue)} onSelect={() => { }} hideButton />
        </div>
      </div>
      <div className="flex flex-col gap-3 p-4 glass-surface relative z-10">
        <h2 className="text-xl font-medium text-text-primary">Datos de contacto de la productora</h2>
        <p>Se generará automáticamente un usuario Admin Productor con los datos que se carguen a continuación. {onBoarding ? 'Puede modificar estos datos desde la  edición del negocio o la  sección Usuarios ' : ''}</p>
        <div className='flex flex-col md:flex-row gap-3'>
          <Controller
            name="legal_name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label="Nombre y apellido / Razón social *"
                id="legal_name"
                labelAbove
                errMsg={errors.legal_name?.message}
              />
            )}
          />
          <Controller
            name="fiscal_id"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label="DNI / CUIT"
                id="fiscal_id"
                labelAbove
                errMsg={errors.fiscal_id?.message}
              />
            )}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label="Correo electrónico *"
                id="email"
                labelAbove
                errMsg={errors.email?.message}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label="Número de celular *"
                id="phone"
                type='phone'
                labelAbove
                errMsg={errors.phone?.message}
              />
            )}
          />
        </div>
        {onBoarding && <div className='relative mt-2'>
          <Controller
            name="accepted_terms"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="accepted_terms"
                isChecked={field.value}
                onCheckChange={field.onChange}
                errMsg={errors.accepted_terms?.message}
              />
            )}
          />
          <p className='absolute top-0 left-8 flex gap-1'>Acepto los <p className='text-primary-500 underline cursor-pointer' onClick={() => setShowTerms(true)}>términos y condiciones</p></p>
        </div>}
        <TermsAndConditions isOpen={showTerms} onClose={() => setShowTerms(false)} type={TermsAndServicesType.SELLER} />
      </div>

      {!onBoarding && userRole === 'ROOT' && (
        <div className="flex flex-col gap-4 p-4 glass-surface">
          <h1 className="text-xl font-medium">Configuración adicional</h1>
          <h1 className='text-xl font-medium'>Visibilidad y permisos</h1>
          <p className='text-sm'>Las productoras activas aparecerán en el listado de productoras y en el panel de control.</p>
          <div>
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Switch
                  label="Activo"
                  labelClassName="text-text-primary"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <>
            <div>
              <Controller
                name="is_reliable"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Productora confiable"
                    labelClassName="text-text-primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                name="is_test"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Productora de prueba"
                    labelClassName="text-text-primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                name="has_members"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Posee sección de socios"
                    labelClassName="text-text-primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                name="has_subscription"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Vende suscripciones"
                    labelClassName="text-text-primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <h1 className='text-xl font-medium'>Pagos</h1>
            <p className='text-sm'>Configuración de pagos de la productora.</p>
            <div>
              <Controller
                name="can_request_advances"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Permite adelantos de dinero"
                    labelClassName="text-text-primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                name="has_cash_registers"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Posee gestión de cajas"
                    labelClassName="text-text-primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div>
              <Controller
                name="has_mp_split"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Mercado Pago Split"
                    labelClassName="text-text-primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <h1 className='text-xl font-medium'>Service charge</h1>
            <p className='text-sm'>Service charge por defecto de los eventos de la productora. En caso de crearse un nuevo evento tiene esos valores.</p>
            <div className='flex flex-col md:flex-row gap-2'>
              <Controller
                name="online_service_charge"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="online_service_charge"
                    label="Service charge online"
                    labelAbove={true}
                    errMsg={errors.online_service_charge?.message}
                  />
                )}
              />
              <Controller
                name="manual_service_charge"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="manual_service_charge"
                    label="Service charge manual"
                    labelAbove={true}
                    errMsg={errors.manual_service_charge?.message}
                  />
                )}
              />
            </div>
            <h1 className='text-xl font-medium'>Información de la productora</h1>
            <div>
              <label htmlFor="logo" className="text-left font-sm font-medium mb-1">
                Logo
              </label>
              <PhotoUpload
                id="logo"
                defaultPreview={retrievedBusiness?.logo}
                onFileSelected={setHasUnsavedPhoto}
                onFileUploaded={(url) => {
                  setValue('logo', url);
                  setHasUnsavedPhoto(false);
                  toastSuccess('Logo actualizado con éxito');
                }}
              />
            </div>
            <div>
              <Switch
                label="Tiene dominio configurado"
                labelClassName="text-text-primary"
                checked={hasDomainSetup}
                onChange={(e) => {
                  setHasDomainSetup(e.target.checked);
                  if (!e.target.checked) {
                    setValue('nickname', null);
                  }
                }}
              />
            </div>
            {hasDomainSetup && (
              <div>
                <Controller name="is_white_label" control={control} render={({ field }) => (
                  <Switch
                    label="Es marca blanca"
                    labelClassName="text-text-primary"
                    checked={field.value || false}
                    onChange={field.onChange}
                  />
                )}/>
                <Controller
                  name="nickname"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      id="nickname"
                      label="Dominio"
                      labelAbove={true}
                      errMsg={errors.nickname?.message}
                    />
                  )}
                />
              </div>
            )}
          </>
        </div>
      )}

      {onBoarding && <Button
        loading={createBusinessRes.isLoading}
        variant="primary"
        type="submit"
        onClick={() => console.log('Button clicked')}
      >Crear productora</Button>}
      {!onBoarding
        && <div className='flex flex-col-reverse md:flex-row gap-4'>
          <Button variant='secondary' onClick={() => navigate(`/admin/${businessData?.id || ''}/events`)}>Cancelar</Button>
          <Button
            loading={createBusinessRes.isLoading || updateBusinessRes.isLoading}
            variant="primary"
            type="submit"
          >
            {create ? 'Crear' : 'Guardar'}
          </Button>
        </div>}

      <CustomModal isOpen={isOpen} onRequestClose={() => { }} hideCancelButton>
        <div className='flex flex-col gap-2 max-w-[358px]'>
          <h1>¡Productora creada con éxito!</h1>
          <p>Ahora vamos a crear tu primer evento</p>
          <p>Actualmente no tenés eventos asociados. Creemos juntos el primer evento de tu productora.</p>
          <div className='flex flex-row gap-2 mt-2'>
            <Button variant='secondary' onClick={() => navigate(`/admin/${businessData?.id}/events`)}>Omitir</Button>
            <Button variant='primary' onClick={() => navigate(`/admin/onboarding/${businessData?.id}/create-event`)}>Crear evento</Button>
          </div>
        </div>
      </CustomModal>
    </form>
  );
};

export default BusinessEditor;
